/**
 * Native (iOS/Android) bridge — safely no-op on web.
 * Called once from App.js on mount.
 */
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { App } from "@capacitor/app";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

export const isNative = () =>
  typeof Capacitor !== "undefined" && Capacitor.isNativePlatform && Capacitor.isNativePlatform();

export { ImpactStyle };

/** Light tap feedback: native haptic, or a short vibration on web where supported. */
export const tapHaptic = (s = ImpactStyle.Light) => {
  if (isNative()) { Haptics.impact({ style: s }).catch(() => {}); return; }
  try { if (typeof navigator !== "undefined") navigator.vibrate?.(15); } catch (_) { /* unsupported */ }
};

/** Success feedback after a confirmed action (native only). */
export const successHaptic = () => {
  if (isNative()) Haptics.notification({ type: NotificationType.Success }).catch(() => {});
};

export const platform = () => (isNative() ? Capacitor.getPlatform() : "web");

/**
 * Sync StatusBar colour with the current theme.
 * @param {"light"|"dark"} theme
 */
export async function applyStatusBar(theme) {
  if (!isNative()) return;
  try {
    await StatusBar.setStyle({
      style: theme === "dark" ? Style.Dark : Style.Light,
    });
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({
        color: theme === "dark" ? "#050505" : "#FFFFFF",
      });
    }
  } catch (e) {
    // status bar plugin failing shouldn't crash the app
    console.warn("StatusBar sync failed", e);
  }
}

/**
 * One-time native boot: hide splash, mark update ready, register deep-link handler.
 */
export async function initNative(navigate) {
  if (!isNative()) return;

  // Tell the hot-update channel that this bundle is healthy
  // (otherwise Capgo will roll back on next start).
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (_) {
    /* Capgo may not be configured in dev — swallow. */
  }

  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (_) { /* no-op */ }

  // Handle universal / deep links (`bhega://…` or `https://…`).
  App.addListener("appUrlOpen", (event) => {
    try {
      const url = new URL(event.url);
      // Strip origin/scheme to convert to an in-app path
      const path = url.pathname + url.search + url.hash;
      if (navigate && path && path !== "/") {
        navigate(path);
      }
    } catch (_) { /* invalid URL */ }
  });
}
